import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, ExternalLink, FileText, CheckCircle2, Circle, Clock, Award, Banknote, ClipboardList, History } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { fmt, today } from "../utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { statusColor } from "./Tenders";
import TenderOperations from "./TenderOperations";

const FLOW = ["Identified", "Documents Preparing", "Submitted", "Technical Qualified", "Financial Opened", "L1", "Awarded", "Rate Contract", "Closed"];
const daysLeft = (d) => d ? Math.ceil((new Date(d) - new Date(new Date().toISOString().slice(0, 10))) / 86400000) : null;

export default function TenderDetail() {
  const { id } = useParams();
  const [td, setTd] = useState(null);
  const [statusModal, setStatusModal] = useState(false);
  const [form, setForm] = useState({ status: "", date: today(), note: "", awarded_value: "", loa_no: "", loa_date: "", emd_status: "" });
  const nav = useNavigate();

  const load = async () => setTd((await api.get(`/tenders/${id}`)).data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => {}); }, [id]);

  if (!td) return <p className="text-sm text-slate-400 py-20 text-center">Loading tender...</p>;

  const stepIdx = Math.max(FLOW.indexOf(td.status), 0);
  const rejected = td.status === "Rejected";
  const docsDone = (td.documents || []).filter((d) => d.done).length;
  const docsTotal = (td.documents || []).length;
  const subDays = daysLeft(td.submission_date);
  const openDays = daysLeft(td.opening_date);

  const saveStatus = async () => {
    if (!form.status) return toast.error("Status select karo");
    try {
      await api.post(`/tenders/${id}/status`, {
        ...form,
        awarded_value: form.awarded_value ? Number(form.awarded_value) : "",
      });
      toast.success("Status updated — timeline mein add ho gaya");
      setStatusModal(false); load();
    } catch (err) { toast.error(err.message); }
  };

  const toggleDoc = async (doc) => {
    try {
      await api.post(`/tenders/${id}/documents`, { key: doc.key, done: !doc.done });
      load();
    } catch (err) { toast.error(err.message); }
  };

  const DayBadge = ({ label, date, days }) => (
    <div className="sgp-card p-4">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" /> {label}</p>
      <p className="font-mono text-sm font-bold text-slate-800 mt-1">{date || "—"}</p>
      {days !== null && (
        <p className={`text-[10px] font-bold mt-0.5 ${days < 0 ? "text-slate-400" : days <= 3 ? "text-rose-600" : "text-amber-600"}`}>
          {days < 0 ? "Date nikal gayi" : days === 0 ? "Aaj hai!" : `${days} din bache`}
        </p>
      )}
    </div>
  );

  return (
    <div data-testid="tender-detail-page" className="sgp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/tenders" data-testid="tender-back-btn" className="p-2 rounded-md hover:bg-slate-100 text-slate-500"><ArrowLeft className="h-4 w-4" /></Link>
          <div>
            <h1 className="text-2xl font-heading font-bold tracking-tight text-slate-900 font-mono">{td.tender_no}</h1>
            <p className="text-sm text-slate-500">{td.department}{td.portal && ` · ${td.portal}`}{td.contact_person && ` · ${td.contact_person}`}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {td.ref_url && (
            <a data-testid="tender-portal-link" href={td.ref_url} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-md border border-slate-300 hover:border-indigo-500 hover:text-indigo-600">
              <ExternalLink className="h-3.5 w-3.5" /> Open Portal
            </a>
          )}
          <button data-testid="tender-update-status-btn" onClick={() => { setForm({ ...form, status: td.status, emd_status: td.emd_status, awarded_value: td.awarded_value || "", loa_no: td.loa_no || "", loa_date: td.loa_date || "" }); setStatusModal(true); }}
            className="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-md bg-teal-700 text-white hover:bg-teal-800">
            <History className="h-3.5 w-3.5" /> Update Status
          </button>
        </div>
      </div>

      {/* Status pipeline */}
      <TenderOperations tender={td} reload={load} />
      <div className="sgp-card p-5 overflow-x-auto" data-testid="tender-pipeline">
        <div className="flex items-center min-w-[720px]">
          {FLOW.map((s, i) => {
            const done = rejected ? false : i <= stepIdx;
            const current = !rejected && i === stepIdx;
            return (
              <div key={s} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center text-center">
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${done ? "bg-teal-600 border-teal-600 text-white" : "bg-white border-slate-300 text-slate-400"} ${current ? "ring-4 ring-teal-100" : ""}`}>
                    {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                  </div>
                  <p className={`text-[9px] font-semibold mt-1 w-20 leading-tight ${done ? "text-teal-700" : "text-slate-400"}`}>{s}</p>
                </div>
                {i < FLOW.length - 1 && <div className={`h-0.5 flex-1 mx-1 -mt-4 ${i < stepIdx ? "bg-teal-500" : "bg-slate-200"}`} />}
              </div>
            );
          })}
        </div>
        {rejected && <p className="mt-3 text-xs font-bold text-rose-600 flex items-center gap-1"><Award className="h-3.5 w-3.5 rotate-180" /> Tender REJECTED — timeline archive mein dekho</p>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <DayBadge label="Submission Last Date" date={td.submission_date} days={subDays} />
        <DayBadge label="Opening Date" date={td.opening_date} days={openDays} />
        <div className="sgp-card p-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Banknote className="h-3 w-3" /> EMD / Security</p>
          <p className="font-mono text-sm font-bold text-slate-800 mt-1">{td.emd ? fmt(td.emd) : "—"}</p>
          {td.emd > 0 && <span className={`text-[10px] font-bold ${td.emd_status === "Refunded" ? "text-emerald-600" : td.emd_status === "Paid" ? "text-blue-600" : "text-amber-600"}`} data-testid="tender-emd-status">{td.emd_status}</span>}
        </div>
        <div className="sgp-card p-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Award className="h-3 w-3" /> Awarded Value</p>
          <p className="font-mono text-sm font-bold text-emerald-700 mt-1" data-testid="tender-awarded-value">{td.awarded_value ? fmt(td.awarded_value) : "—"}</p>
          {td.loa_no && <p className="text-[10px] font-mono text-slate-500 mt-0.5">LOA: {td.loa_no} ({td.loa_date})</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Documents checklist */}
        <div className="sgp-card p-5" data-testid="tender-documents">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2"><ClipboardList className="h-4 w-4 text-indigo-600" /> Documents Checklist</h3>
            <span className="text-xs font-mono font-bold text-slate-600" data-testid="docs-progress">{docsDone}/{docsTotal}</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 mb-4 overflow-hidden">
            <div className="h-full bg-teal-600 transition-all" style={{ width: `${docsTotal ? (docsDone / docsTotal) * 100 : 0}%` }} />
          </div>
          <div className="space-y-1">
            {(td.documents || []).map((d) => (
              <button key={d.key} data-testid={`doc-${d.key}`} onClick={() => toggleDoc(d)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left text-sm transition-colors ${d.done ? "bg-emerald-50/60 text-slate-700" : "hover:bg-slate-50 text-slate-600"}`}>
                {d.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <Circle className="h-4 w-4 text-slate-300 shrink-0" />}
                <span className={d.done ? "line-through text-slate-400" : ""}>{d.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="sgp-card p-5" data-testid="tender-timeline">
          <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2 mb-4"><History className="h-4 w-4 text-teal-700" /> Live Tracking Timeline</h3>
          <div className="space-y-0">
            {[...(td.status_history || [])].reverse().map((h, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className={`h-2.5 w-2.5 rounded-full mt-1.5 ${i === 0 ? "bg-teal-600 ring-4 ring-teal-100" : "bg-slate-300"}`} />
                  {i < td.status_history.length - 1 && <div className="w-px flex-1 bg-slate-200" />}
                </div>
                <div className="pb-4">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusColor[h.status] || statusColor.Draft}`}>{h.status}</span>
                    <span className="text-[10px] font-mono text-slate-400">{h.date}</span>
                  </div>
                  {h.note && <p className="text-xs text-slate-500 mt-1">{h.note}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {(td.items_text || td.notes) && (
        <div className="sgp-card p-5 space-y-2">
          {td.items_text && <div><p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Items & Rates</p><p className="text-sm text-slate-700 whitespace-pre-line">{td.items_text}</p></div>}
          {td.notes && <div><p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Notes</p><p className="text-sm text-slate-600">{td.notes}</p></div>}
        </div>
      )}

      <Dialog open={statusModal} onOpenChange={setStatusModal}>
        <DialogContent data-testid="tender-status-modal" className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Update Status — {td.tender_no}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2"><label className="sgp-label">New Status</label>
              <select data-testid="status-select" className="sgp-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {[...new Set([...FLOW, "Technical Disqualified", "L2", "L3", "Lost", td.status])].map((s) => <option key={s}>{s}</option>)}
              </select></div>
            <div><label className="sgp-label">Date</label>
              <input data-testid="status-date" type="date" className="sgp-input font-mono" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><label className="sgp-label">EMD Status</label>
              <select data-testid="status-emd" className="sgp-input" value={form.emd_status} onChange={(e) => setForm({ ...form, emd_status: e.target.value })}>
                {["Pending", "Paid", "Refunded", "Exempted"].map((s) => <option key={s}>{s}</option>)}
              </select></div>
            {form.status === "Awarded" && (
              <>
                <div><label className="sgp-label">Awarded Value (₹)</label>
                  <input data-testid="status-awarded-value" type="number" className="sgp-input font-mono" value={form.awarded_value} onChange={(e) => setForm({ ...form, awarded_value: e.target.value })} /></div>
                <div><label className="sgp-label">LOA / Rate Contract No.</label>
                  <input data-testid="status-loa-no" className="sgp-input font-mono" value={form.loa_no} onChange={(e) => setForm({ ...form, loa_no: e.target.value })} /></div>
                <div><label className="sgp-label">LOA Date</label>
                  <input data-testid="status-loa-date" type="date" className="sgp-input font-mono" value={form.loa_date} onChange={(e) => setForm({ ...form, loa_date: e.target.value })} /></div>
              </>
            )}
            <div className="col-span-2"><label className="sgp-label">Note (timeline mein dikhega)</label>
              <input data-testid="status-note" className="sgp-input" placeholder="e.g. Technical bid mein qualify ho gaye" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            <button data-testid="status-save-btn" onClick={saveStatus}
              className="col-span-2 py-2 text-sm font-semibold rounded-md bg-teal-700 text-white hover:bg-teal-800">Save Status</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
